import React from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem 2rem 0.5rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 3px solid var(--primary-color);
`;

const Section = styled.section`
  margin-bottom: 1.75rem;
  padding: 0;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  color: #444;
  margin-bottom: 0.75rem;
  font-weight: 600;
`;

const SubSectionTitle = styled.h3`
  font-size: 1.35rem;
  color: #444;
  margin-top: 0.5rem;
  margin-bottom: 0.3rem;
  font-weight: 600;
`;

const Content = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  color: #555;
  
  p {
    margin-bottom: 0.5rem;
  }
  
  ul, ol {
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.25rem;
    }
  }
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LastUpdated = styled.p`
  font-style: italic;
  color: #777;
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const PrivacyPage: React.FC = () => {
  return (
    <PageContainer className="font--jakarta">
      <SEO 
        title="Privacy Policy"
        description="UPTREND Systems Privacy Policy describes how we collect, use, and protect your personal information when using our software development services or website."
        keywords="privacy policy, data protection, PIPEDA compliance, personal information privacy, software company privacy, UPTREND Systems privacy policy"
        canonicalUrl="/privacy"
        noIndex={false}
        pageType="legal"
        location="Whitby"
      />
      <PageTitle>Privacy Policy</PageTitle>
      
      <Section>
        <Content>
          <p>At Uptrend Systems, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>1. Collection of Your Information</SectionTitle>
        <Content>
          <p>We may collect information about you in a variety of ways. The information we may collect via the website includes:</p>
          
          <SubSectionTitle>Personal Data</SubSectionTitle>
          <p>When you schedule an appointment or contact us, we collect personal information that you voluntarily provide to us, such as your name, email address, phone number, and any other information you choose to provide. This information is used solely for the purpose of providing you with the requested services, such as setting appointments or responding to your inquiries.</p>
          
          <SubSectionTitle>Google Analytics Data</SubSectionTitle>
          <p>We use Google Analytics to help us understand how our visitors use our website. Google Analytics collects standard internet log information and visitor behavior information. This information is processed in a way that does not directly identify anyone. We do not use Google Analytics to collect any personally identifiable information about you, nor do we combine the general information collected through analytics with other data to identify individuals.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>2. Use of Your Information</SectionTitle>
        <Content>
          <p>Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the website to:</p>
          <ul>
            <li>Create and manage your appointments</li>
            <li>Respond to your inquiries and customer service requests</li>
            <li>Email you regarding your appointment or services</li>
            <li>Monitor and analyze usage and trends to improve your experience with the website</li>
            <li>Improve our website and services</li>
          </ul>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>3. Cookies and Web Beacons</SectionTitle>
        <Content>
          <p>We may use cookies and web beacons on the website to help customize the website and improve your experience. A cookie is a small file placed on your device. Web beacons (also known as clear gifs, pixel tags, and single-pixel gifs) are small electronic files contained on pages of websites. We utilize these technologies to collect data about your browsing actions and patterns.</p>
          <p>Third-party analytics services (such as Google Analytics) may use cookies and similar technologies to collect and analyze information about website use and to report on activities and trends. These services may also collect information regarding the use of other websites, apps, and online resources.</p>
          <p>You can refuse cookies by turning them off in your browser. However, if you turn off cookies, some features of the website may not function properly.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>4. Third-Party Websites</SectionTitle>
        <Content>
          <p>The website may contain links to third-party websites and applications of interest, including advertisements and external services, that are not affiliated with us. Once you have used these links to leave the website, any information you provide to these third parties is not covered by this Privacy Policy, and we cannot guarantee the safety and privacy of your information. Before visiting and providing any information to any third-party websites, you should inform yourself of the privacy policies and practices (if any) of the third party responsible for that website, and should take those steps necessary to, in your discretion, protect the privacy of your information. We are not responsible for the content or privacy and security practices and policies of any third parties, including other sites, services or applications that may be linked to or from the website.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>5. Security of Your Information</SectionTitle>
        <Content>
          <p>We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse. Any information disclosed online is vulnerable to interception and misuse by unauthorized parties. Therefore, we cannot guarantee complete security if you provide personal information.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>6. Policy for Children</SectionTitle>
        <Content>
          <p>We do not knowingly solicit information from or market to children under the age of 13. If you become aware of any data we have collected from children under age 13, please contact us using the contact information provided below.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>7. Your Rights Under Personal Information Protection and Electronic Documents Act (PIPEDA)</SectionTitle>
        <Content>
          <p>If you are a resident of Canada, you have the right to access personal information we hold about you and to ask that your personal information be corrected, updated, or deleted. If you would like to exercise this right, please contact us through the contact information below.</p>
          <p>Additionally, please note that your information will be transferred outside of Canada, including to the United States if needed to provide services to you.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>8. Contact Us</SectionTitle>
        <Content>
          <p>If you have questions or comments about this Privacy Policy, please contact us at:</p>
          <p>
            Uptrend Systems<br />
            info@uptrendsystems.com
          </p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>9. Changes to This Privacy Policy</SectionTitle>
        <Content>
          <p>We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last Updated" date and the updated version will be effective as soon as it is accessible. We encourage you to review this Privacy Policy frequently to be informed of how we are protecting your information.</p>
        </Content>
      </Section>
      
      <LastUpdated>Last Updated: January 4, 2025</LastUpdated>
    </PageContainer>
  );
};

export default PrivacyPage; 